import { cn } from '@sparklayer-io/spark-dash-ui/lib/styles';
import { trimProtocol } from '../lib/trim-protocol';

type FloatingInputOption = { value: string; text: string; disabled?: boolean };

type FloatingInputProps = {
  id?: string;
  name: string;
  label: string;
  type?: 'text' | 'email' | 'password' | 'select';
  autoComplete?: string;
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  disabled?: boolean;
  /** Static text rendered in an attached box before the field (e.g. `https://`). */
  prefix?: string;
  /** Options for `type="select"`. */
  options?: FloatingInputOption[];
  value: string;
  onChange: (value: string) => void;
};

const LABEL_BASE =
  'absolute top-2 z-10 origin-left -translate-y-4 transform bg-spark-surface px-2 text-spark-text-tertiary text-sm duration-300 peer-focus:top-2 peer-focus:-translate-y-4 peer-focus:bg-spark-surface peer-focus:px-2 peer-focus:text-spark-accent';

export function FloatingInput({
  id,
  name,
  label,
  type = 'text',
  autoComplete,
  required,
  minLength,
  maxLength,
  disabled,
  prefix,
  options,
  value,
  onChange,
}: FloatingInputProps) {
  const fieldId = id ?? name;

  if (type === 'select') {
    const selectOptions = options ?? [];
    return (
      <div className="relative">
        <select
          id={fieldId}
          name={name}
          value={value}
          required={required}
          disabled={disabled ?? selectOptions.length === 0}
          className="peer block w-full cursor-pointer appearance-none rounded-md border border-spark-border-default bg-spark-surface px-3 pt-3 pb-2 text-sm text-spark-text-primary shadow-xs invalid:text-spark-text-tertiary focus:border-spark-accent-hover focus:outline-hidden focus:ring-spark-accent-hover disabled:cursor-not-allowed disabled:bg-spark-surface-muted disabled:text-spark-text-secondary"
          onChange={(event) => onChange(event.target.value)}
        >
          {selectOptions.map((option) => (
            <option
              key={option.value}
              value={option.value}
              disabled={option.disabled}
              className={cn(
                option.disabled ? 'text-spark-text-tertiary' : 'text-spark-text-primary',
              )}
            >
              {option.text}
            </option>
          ))}
        </select>
        <label
          htmlFor={fieldId}
          className={cn(
            'pointer-events-none absolute inset-s-3 z-10 origin-left transform bg-spark-surface px-2 text-sm text-spark-text-tertiary duration-300 peer-focus:top-2 peer-focus:ms-0 peer-focus:-translate-y-4 peer-focus:px-2 peer-focus:text-spark-accent',
            value !== '' ? 'top-2 -translate-y-4' : 'top-2.75 -ms-2 scale-100',
          )}
        >
          {label}
        </label>
      </div>
    );
  }

  return (
    <div className={cn('relative', prefix && 'flex')}>
      {prefix && (
        <span className="inline-flex items-center rounded-s-md border border-spark-border-default border-e-0 bg-spark-surface-subtle px-3 text-spark-text-secondary sm:text-sm">
          {prefix}
        </span>
      )}
      <input
        id={fieldId}
        name={name}
        type={type}
        maxLength={maxLength}
        minLength={minLength}
        autoComplete={autoComplete}
        placeholder=" "
        required={required}
        value={value}
        disabled={disabled}
        className={cn(
          'peer block w-full appearance-none border border-spark-border-default bg-spark-surface px-3 pt-3 pb-2 text-sm text-spark-text-primary shadow-xs focus:border-spark-accent-hover focus:outline-hidden focus:ring-spark-accent-hover disabled:bg-spark-surface-muted disabled:text-spark-text-secondary',
          prefix ? 'rounded-none rounded-e-md' : 'rounded-md',
        )}
        onChange={(event) => onChange(event.target.value)}
        onPaste={
          prefix
            ? (event) => {
                // The prefix already shows the scheme, so strip it from pasted URLs
                // even when they land after existing text (plain onChange only
                // trims a leading match).
                const pasted = event.clipboardData.getData('text');
                const stripped = trimProtocol(pasted);
                if (stripped === pasted) return;
                event.preventDefault();
                const input = event.currentTarget;
                const start = input.selectionStart ?? value.length;
                const end = input.selectionEnd ?? value.length;
                onChange(value.slice(0, start) + stripped + value.slice(end));
              }
            : undefined
        }
      />
      <label
        htmlFor={fieldId}
        className={cn(
          LABEL_BASE,
          'peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:scale-100 peer-placeholder-shown:text-spark-text-tertiary',
          prefix
            ? 'inset-s-18.75 ms-2 flex items-center gap-x-1'
            : 'inset-s-3 peer-placeholder-shown:bg-transparent peer-placeholder-shown:px-0',
        )}
      >
        {label}
      </label>
      {maxLength && (
        <div className="absolute inset-e-3 top-2.25 z-10 text-sm text-spark-text-secondary">
          {value.length}/{maxLength}
        </div>
      )}
    </div>
  );
}
